import styled from '@emotion/styled'
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons'
import { faChevronDown, faChevronUp, faPen, faStar, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import UserContext from '../../context/UserContext'
import { apiClient } from '../../services/apiClient'
import { SkillStatusEnum } from '../../types/skill.type'
import { renderHtmlContent } from '../../utils/htmlRenderer'
import { extractYouTubeId, YouTubeEmbed } from './YouTubeEmbed'

type SkillProps = {
    id?: number
    name: string
    comment?: string
    status?: SkillStatusEnum
    isFavorite?: boolean
    refetchMySkills?: () => void
    redirect?: () => void
    isInWhiteBackground?: true
    deleteFn?: () => void
    editFn?: () => void
    parentSkill?: {
        id: number
        name: string
    }
}

export const skillStatusColors: Record<SkillStatusEnum, string> = {
    [SkillStatusEnum.TO_DISCOVER]: '#aaaaaa',
    [SkillStatusEnum.STARTED]: '#FFC107',
    [SkillStatusEnum.CONFIRMED]: '#8BC34A',
    [SkillStatusEnum.MASTERED]: '#337AB7',
}

const ParentSkillTag = styled.span`
    background-color: #e9ecef;
    color: #495057;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 1.5rem;
    font-weight: 500;
    width: fit-content;
`

const Styled = {
    EditIcon: styled(FontAwesomeIcon)`
        cursor: pointer;
        margin-right: 16px;
        background-color: var(--bs-btn-hover-bg);
        scale: 1.5;
    `,

    TrashIcon: styled(FontAwesomeIcon)`
        cursor: pointer;
        color: var(--primary-color);
        scale: 1.5;
    `,

    FavoriteBtn: styled.button`
        background-color: transparent;
        border: none;
        appearance: none;
        cursor: pointer;
    `,

    BadgeContainer: styled.div`
        display: flex;
        align-items: center;
    `,

    DropDownIcon: styled.div`
        margin-left: 8px;
    `,

    TitleWrapper: styled.div`
        display: flex;
        justify-content: space-between;
        align-items: center;
    `,

    Card: styled.div<{ isClickable?: boolean; isInWhiteBackground?: true }>`
        position: relative;
        ${({ isClickable }) => (isClickable ? 'cursor: pointer;' : '')}
        ${({ isInWhiteBackground }) =>
            isInWhiteBackground ? 'background-color: #f7f7f7;' : 'background-color: white;'}
        ${({ isInWhiteBackground }) => (isInWhiteBackground ? 'border: 1px solid #ddd;' : '')}
        border-radius: 5px;
        padding: 20px;
        height: min-content;

        &:hover {
            ${({ isClickable }) => (isClickable ? 'background-color: #eee;' : '')}
        }
    `,

    CardTitle: styled.div<{ isClickable?: boolean }>`
        ${({ isClickable }) => (isClickable ? 'cursor: pointer;' : '')}
        ${({ isClickable }) => (!isClickable ? 'user-select: none;' : '')}
        ${({ isClickable }) => (isClickable ? 'text-decoration: underline;' : '')}
        font-weight: bold;
        margin-right: 8px;

        &:hover {
            ${({ isClickable }) => (isClickable ? 'color: #0066cc;' : '')}
        }
    `,

    Dropdown: styled.div`
        position: relative;
        padding: 10px;
    `,

    Badge: styled.span<{ backgroundColor?: string }>`
        user-select: none;
        background-color: ${(props) => props.backgroundColor ?? '#0066cc'};
        color: #ffffff;
        padding: 5px 10px;
        border-radius: 5px;
        font-size: 14px;
        margin-right: 8px;
    `,
}

const extractYouTubeVideoId = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
    const match = RegExp(regExp).exec(url)
    return match && match[2].length === 11 ? match[2] : null
}

const addTextContent = (result: (JSX.Element | string)[], text: string, key: string) => {
    if (!text.trim()) return
    const htmlContent = renderHtmlContent(text)
    result.push(<div key={key} dangerouslySetInnerHTML={htmlContent} />)
}

const processIframes = (
    comment: string,
    result: (JSX.Element | string)[],
): { lastIndex: number; hasIframes: boolean } => {
    const iframeRegex = /<iframe[^>]*src=["']([^"']*youtube[^"']*)[^>]*>/g
    let match
    let lastIndex = 0
    let hasIframes = false

    while ((match = iframeRegex.exec(comment)) !== null) {
        hasIframes = true

        if (match.index > lastIndex) {
            addTextContent(result, comment.substring(lastIndex, match.index), `text-before-${match.index}`)
        }
        const videoId = extractYouTubeVideoId(match[1])
        if (videoId) {
            result.push(
                <div key={`yt-${videoId}`} style={{ margin: '1rem 0' }}>
                    <YouTubeEmbed videoId={videoId} />
                </div>,
            )
        }
        lastIndex = match.index + match[0].length
    }

    return { lastIndex, hasIframes }
}

const processDirectUrls = (comment: string, result: (JSX.Element | string)[], startIndex: number): number => {
    const youtubeRegex = /(https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=|shorts\/)?([\w-]{11}))/g
    let match
    let lastIndex = startIndex

    while ((match = youtubeRegex.exec(comment)) !== null) {
        if (match.index > lastIndex) {
            addTextContent(result, comment.substring(lastIndex, match.index), `text-before-url-${match.index}`)
        }

        const videoId = extractYouTubeId(match[0])
        if (videoId) {
            result.push(
                <div key={`yt-${videoId}`} style={{ margin: '1rem 0' }}>
                    <YouTubeEmbed videoId={videoId} />
                </div>,
            )
        }
        lastIndex = match.index + match[0].length
    }

    return lastIndex
}

const processRemainingText = (comment: string, currentIndex: number, result: (JSX.Element | string)[]) => {
    if (currentIndex < comment.length) {
        const remainingText = comment.substring(currentIndex)
        const cleanText = remainingText.replace(/<iframe[^>]*>[\s\S]*?<\/iframe>/g, '')
        if (cleanText.trim()) {
            addTextContent(result, cleanText, 'remaining-text')
        }
    }
}

const CommentContent = ({ comment }: { comment: string }) => {
    const renderYouTubeVideos = (text: string) => {
        const result: (JSX.Element | string)[] = []
        const { lastIndex, hasIframes } = processIframes(comment, result)
        let currentIndex = hasIframes ? lastIndex : processDirectUrls(comment, result, 0)

        processRemainingText(comment, currentIndex, result)

        return result.length > 0 ? result : comment
    }

    const commentStyle = {
        whiteSpace: 'pre-wrap' as const,
        borderTop: '1px solid #ccc',
        marginTop: '8px',
        padding: '10px',
        width: '100%',
        overflow: 'hidden' as const,
    }

    try {
        const content = renderYouTubeVideos(comment)
        return <div style={commentStyle}>{content}</div>
    } catch (error) {
        console.error('Error parsing comment:', error)
        const htmlContent = renderHtmlContent(comment)
        return <div style={commentStyle} dangerouslySetInnerHTML={htmlContent} />
    }
}

const SkillCard = ({
    id,
    name,
    comment,
    status,
    isFavorite,
    refetchMySkills,
    isInWhiteBackground,
    redirect,
    deleteFn,
    editFn,
    parentSkill,
}: SkillProps) => {
    const user = useContext(UserContext)
    const [isOpen, setIsOpen] = useState(false)

    const { mutate: skillIsFavoriteMutation } = useMutation({
        mutationFn: (isFavorite: boolean) =>
            apiClient
                .patch(`/skill-users/${id}`, {
                    isFavorite,
                })
                .then(() => refetchMySkills?.())
                .catch((error) => {
                    console.error('Error updating favorite status:', error)
                    toast.error('Failed to update favorite status')
                }),
    })

    const { t } = useTranslation('translation')

    const handleTitleClick = (e: React.MouseEvent) => {
        if (redirect) {
            e.stopPropagation()
            e.preventDefault()
            redirect()
        }
    }

    const handleFavoriteClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        skillIsFavoriteMutation(!isFavorite)
    }

    return (
        <Styled.Card
            onClick={() => setIsOpen(!isOpen)}
            isClickable={!!comment}
            isInWhiteBackground={isInWhiteBackground}
        >
            <Styled.TitleWrapper>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {parentSkill && <ParentSkillTag>{parentSkill.name}</ParentSkillTag>}
                    <Styled.CardTitle isClickable={!!redirect} onClick={handleTitleClick}>
                        {name}
                    </Styled.CardTitle>
                </div>
                <Styled.BadgeContainer>
                    {status && (
                        <Styled.Badge backgroundColor={skillStatusColors[status]}>{t(`skill.${status}`)}</Styled.Badge>
                    )}

                    {isFavorite !== undefined && (
                        <Styled.FavoriteBtn onClick={handleFavoriteClick}>
                            <FontAwesomeIcon icon={isFavorite ? faStar : faStarRegular} />
                        </Styled.FavoriteBtn>
                    )}
                    {editFn && <Styled.EditIcon icon={faPen} onClick={editFn} />}
                    {deleteFn && <Styled.TrashIcon icon={faTrash} onClick={deleteFn} />}
                    {comment && (
                        <Styled.DropDownIcon>
                            <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                        </Styled.DropDownIcon>
                    )}
                </Styled.BadgeContainer>
            </Styled.TitleWrapper>

            {isOpen && comment && (
                <Styled.Dropdown>
                    <div>Commentaire</div>
                    <CommentContent comment={comment} />
                </Styled.Dropdown>
            )}
        </Styled.Card>
    )
}

export default SkillCard
